/**
 * EntityCombobox — typeahead select for picking one entity from a
 * remote list (customer, category, etc.).
 *
 * Pattern: a `Popover`-shelled dropdown wrapping a `cmdk` `Command`
 * filter list. The caller supplies an async fetch keyed on the
 * search string; the component debounces it and renders item names
 * with their id underneath. A caller whose list is paged server-side
 * supplies `fetchPage` instead, and the list offers "Show more" until
 * the last page is in.
 *
 * Designed for "discovery from a long list" use cases (~1000s of
 * rows). Not suitable for free entry of unlisted ids — when the
 * caller needs to refer to something that doesn't exist yet, a
 * plain `Input` is the right primitive.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown, Loader2, X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../primitives/command";
import { Popover, PopoverContent, PopoverTrigger } from "../primitives/popover";
import { Button } from "../primitives/button";
import { cn } from "../primitives/utils";

export interface EntityOption {
  id: string;
  label: string;
  /** Optional secondary text (e.g. customer name when id is shown). */
  hint?: string;
}

/** One page of options from a server-paged list. */
export interface EntityOptionPage {
  options: EntityOption[];
  /** Whether a further page exists for the same query. */
  hasMore: boolean;
}

/** Where the options come from: one fetch per query, or pages of it. */
type EntityComboboxSource =
  | {
      /** Loads matching options for the user's typed search string. The
       *  callback receives the trimmed query (empty string for an open
       *  popover with no input yet). Should return a small page; the
       *  component does not paginate further. */
      fetchOptions: (query: string) => Promise<EntityOption[]>;
      fetchPage?: never;
    }
  | {
      /** Loads page `page` (1-based) of the options matching the trimmed
       *  query, for a list paged server-side. */
      fetchPage: (query: string, page: number) => Promise<EntityOptionPage>;
      fetchOptions?: never;
    };

export type EntityComboboxProps = EntityComboboxSource & {
  /** Currently-selected entity, or null when empty. */
  value: EntityOption | null;
  onChange: (option: EntityOption | null) => void;
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  /** The search box's placeholder, where it differs from the trigger's (a list filter's "Customer: any"). */
  searchPlaceholder?: string;
  /** Label rendered next to the trigger button (visually hidden when
   *  caller wraps in their own label element). */
  ariaLabel?: string;
  /** Disable the input entirely (e.g. during save). */
  disabled?: boolean;
  /** Show a clear-selection (×) affordance on the trigger when a
   *  value is set. Defaults to true. */
  clearable?: boolean;
  /** Class for the trigger button — opt-in width override. */
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  /** What the list says when the lookup found nothing; defaults to "No matches". */
  emptyText?: string;
  /** Offered only after a successful lookup returns no matches. */
  onCreate?: () => void;
  createLabel?: string;
};

const FETCH_DEBOUNCE_MS = 200;
const LOAD_MORE_VALUE = "__entity_combobox_load_more__";

export function EntityCombobox({
  value,
  onChange,
  fetchOptions,
  fetchPage,
  placeholder = "Start typing…",
  searchPlaceholder,
  ariaLabel,
  disabled = false,
  clearable = true,
  className,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  emptyText = "No matches",
  onCreate,
  createLabel = "Create new",
}: EntityComboboxProps) {
  const triggerId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<EntityOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Paged lists only: the last page loaded for the current query, and
  // whether another follows it.
  const [loadedPage, setLoadedPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const fetchSeq = useRef(0);

  const loadPage = useCallback(
    (q: string, page: number): Promise<EntityOptionPage> =>
      fetchPage
        ? fetchPage(q, page)
        : // The props union guarantees fetchOptions when fetchPage is absent.
          fetchOptions!(q).then((all) => ({ options: all, hasMore: false })),
    [fetchOptions, fetchPage],
  );

  // The query and source the list's first page was last fetched for, while
  // the popover is open.
  const lastFirstPage = useRef<{
    query: string;
    source: typeof loadPage;
  } | null>(null);

  // Page 1 replaces the list (a new query); a later page appends to it.
  const runFetch = useCallback(
    async (q: string, page = 1) => {
      const seq = ++fetchSeq.current;
      if (page === 1) lastFirstPage.current = { query: q, source: loadPage };
      setLoading(true);
      setError(null);
      try {
        const next = await loadPage(q, page);
        // Only apply this result if we're still the latest in-flight fetch.
        if (seq === fetchSeq.current) {
          setOptions((prev) =>
            page === 1 ? next.options : [...(prev ?? []), ...next.options],
          );
          setLoadedPage(page);
          setHasMore(next.hasMore);
        }
      } catch (e) {
        if (seq === fetchSeq.current) {
          setError(e instanceof Error ? e.message : "Lookup failed");
          if (page === 1) setOptions([]);
          setHasMore(false);
        }
      } finally {
        if (seq === fetchSeq.current) {
          setLoading(false);
        }
      }
    },
    [loadPage],
  );

  // Initial load when popover opens with no options yet.
  useEffect(() => {
    if (open && options === null) {
      void runFetch("");
    }
  }, [open, options, runFetch]);

  // Debounced refetch when the user types, when the source changes, and on
  // every reopen. It skips a list it already holds for this query and
  // source: otherwise the first open would load page 1 twice, and the second
  // load would land after (and wipe) a "Show more" clicked in between.
  useEffect(() => {
    if (!open) {
      lastFirstPage.current = null;
      return;
    }
    const handle = setTimeout(() => {
      const q = query.trim();
      const last = lastFirstPage.current;
      if (last && last.query === q && last.source === loadPage) return;
      void runFetch(q);
    }, FETCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, open, runFetch, loadPage]);

  const triggerLabel = useMemo(() => {
    if (value) return value.label;
    return placeholder;
  }, [value, placeholder]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={triggerId}
          type="button"
          variant="outline"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <span className="flex items-center gap-1 ml-2 shrink-0">
            {value && clearable && !disabled && (
              <span
                role="button"
                aria-label="Clear selection"
                className="hover:bg-accent rounded p-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(null);
                  }
                }}
                tabIndex={0}
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder ?? placeholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            )}
            {error && (
              <div className="px-3 py-2 text-xs text-red-700">{error}</div>
            )}
            {!loading && !error && options && options.length === 0 && (
              <CommandEmpty>
                {emptyText}
                {onCreate && (
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-2"
                    onClick={() => {
                      setOpen(false);
                      onCreate();
                    }}
                  >
                    {createLabel}
                  </Button>
                )}
              </CommandEmpty>
            )}
            {options && options.length > 0 && (
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.id}
                    value={opt.id}
                    onSelect={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                  >
                    <div className="flex flex-col">
                      <span>{opt.label}</span>
                      {opt.hint && (
                        <span className="text-[11px] text-muted-foreground">
                          {opt.hint}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
                {hasMore && (
                  <CommandItem
                    value={LOAD_MORE_VALUE}
                    disabled={loading}
                    onSelect={() => void runFetch(query.trim(), loadedPage + 1)}
                    className="justify-center text-xs text-primary"
                  >
                    Show more
                  </CommandItem>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
